import type { TFunction } from 'i18next'
import type { FooterColumnItem, FooterColumnType } from '../types'
import { FooterCookiesConfig } from '../ui/components/footer-cookies-config/FooterCookiesConfig'
import {
  createCustomComponent,
  createInstagramLink,
  createLink,
  createStoreLink,
  createStoreLinkGroup,
  createTikTokLink,
} from './helpers'
import { getConfig } from '@/tools/config'
import type { Language } from '@/tools/i18n'

export const getColumns = (
  t: TFunction,
  language: Language,
  countryCode: string | null,
  isLoggedIn: boolean
) => {
  const { appUrl } = getConfig()
  const lowerCaseCountryCode = countryCode?.toLowerCase()
  const doItTogetherColumn: FooterColumnType = {
    title: t('footer.lets-do-it-together'),
    items: [
      createLink(t('footer.careers'), 'https://jobs.glovoapp.com'),
      createLink(t('footer.partners'), 'https://sell.glovoapp.com'),
      createLink(t('footer.couriers'), 'https://couriers.glovoapp.com/'),
      createLink(t('footer.glovo-business'), 'https://business.glovoapp.com'),
    ],
  }

  const linksOfInterestItems = [
    createLink(t('footer.about-us'), 'https://about.glovoapp.com'),
    createLink(t('common.faq'), `https://glovoapp.com/docs/${language}/faq`),
    lowerCaseCountryCode
      ? createLink(
          t('footer.glovo-prime'),
          `https://glovoapp.com/${lowerCaseCountryCode}/${language}/prime/`
        )
      : null,
    createLink(t('common.blog'), 'https://about.glovoapp.com/stories/'),
    createLink(t('ecommerce.actions.contactus'), `https://glovoapp.com/${language}/contact-us/`),
    createLink(t('footer.security'), `/${language}/security/`),
    isLoggedIn
      ? createLink(t('profile.title'), `${appUrl}/${language}/profile`)
      : createLink(t('login.title'), `${appUrl}/${language}/login`),
  ].filter((link) => link !== null)

  const linksOfInterest: FooterColumnType = {
    title: t('footer.links-of-interest'),
    items: linksOfInterestItems as FooterColumnItem[],
  }

  const followUsItems = [
    createInstagramLink(lowerCaseCountryCode),
    createTikTokLink(lowerCaseCountryCode),
  ].filter((link) => link !== null)

  const followUsColumn: FooterColumnType = {
    title: t('footer.follow-us'),
    items: followUsItems as FooterColumnItem[],
  }

  const storesAndPoliciesColumn: FooterColumnType = {
    items: [
      createStoreLinkGroup([
        createStoreLink(
          'https://app.adjust.com/xle4el?adgroup=&campaign=footer_null_en',
          'https://glovoapp.com/images/app_store/download-button-new.svg',
          105,
          35
        ),
        createStoreLink(
          'https://app.adjust.com/ule61n?redirect=http%3A%2F%2Fplay.google.com/store/apps/details?id=com.glovo&hl=es&adgroup=&campaign=footer_null_en',
          'https://glovoapp.com/images/google_play/download-button-new.svg',
          112,
          35
        ),
      ]),
      createLink(t('footer.terms'), `https://glovoapp.com/docs/${language}/legal/terms/`),
      createLink(t('footer.privacy'), `https://glovoapp.com/docs/${language}/legal/privacy/`),
      createLink(t('signup.cookies'), `https://glovoapp.com/${language}/legal/cookies/`),
      createLink(t('footer.compliance'), 'https://compliance.glovoapp.com/public/home'),
      createCustomComponent(FooterCookiesConfig),
    ],
  }

  const columns = [
    doItTogetherColumn,
    linksOfInterest,
    followUsColumn,
    storesAndPoliciesColumn,
  ].filter((column) => column.items.length > 0)

  return columns
}
